import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, App, Button, Form, Spin } from 'antd';
import { Repeat } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PAGINATION } from '@pams/shared';
import type {
  OrgUnitSummaryDto,
  Paginated,
  PolicyListItemDto,
  PolicyVersionDto,
  PostTypeSummaryDto,
  RequirementLineInputDto,
  StandingOrderDto,
  StandingOrderPreviewDto,
  VehicleTypeSummaryDto,
} from '@pams/shared';
import { IntakeForm } from '@/components/intake/IntakeForm';
import { PageHeader } from '@/components/PageHeader';
import { useAuth } from '@/hooks/useAuth';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { can } from '@/lib/accessRole';
import { api, type ApiError } from '@/lib/api';
import { toGrid } from '@/lib/forceGrid';
import { queryKeys } from '@/lib/queryKeys';
import { notifyDevChange } from '@/lib/devNotice';
import { cn } from '@/lib/utils';
import { checklistOf, stepOfSection } from '@/lib/sectionRegistry';
import { RTC_COPY } from './copy';
import {
  emptyStandingOrder,
  fromStandingOrder,
  toStandingOrderBody,
  toStandingOrderDraft,
  type StandingOrderFormValues,
} from './formValues';
import {
  RTC_STEPS,
  STANDING_ORDER_SECTIONS,
  type StandingOrderSectionActions,
  type StandingOrderSectionState,
} from './sections';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  THE ROUND-THE-CLOCK BUILDER — the same shell as the PA intake, over a different
 *  registry. Everything about which sections exist, what order they come in and what each
 *  still needs is in `sections.tsx`.
 *
 *  ⚠️ AN EXPLICIT SAVE, AND THIS IS THE ONE PLACE P3b.1 DELIBERATELY DEPARTS FROM THE PA
 *  INTAKE. It is not taste.
 *
 *  The arrangement builder autosaves because a PA draft is private and cheap. Here, SAVING
 *  IS WHAT MAKES THE GENERATOR RUN: an 800 ms debounce mid-keystroke would materialise real
 *  arrangements that a division sees, from a configuration with a 22-hour hole in it that
 *  the operator was two keystrokes from closing. The status set says the same thing — there
 *  is `ACTIVE` and `ENDED` and no draft state to autosave INTO.
 *
 *  `useUnsavedGuard` is therefore genuinely load-bearing here rather than a backstop.
 */
const StandingOrderBuilderPage = () => {
  const { standingOrderId } = useParams<{ standingOrderId: string }>();
  const { officer } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { message, modal } = App.useApp();

  const canWrite = officer ? can(officer.accessRole, 'standingOrder.write') : false;
  const readOnly = !canWrite;

  const [form] = Form.useForm<StandingOrderFormValues>();
  const [step, setStep] = useState(0);
  const [dirty, setDirty] = useState(false);
  const sectionRefs = useRef(new Map<string, HTMLElement>());

  /**
   * ⚠️ [D4.5 §1b] `show: 'all'`, WHICH DOES NOT WIDEN WHAT MAY BE CHOSEN — see the twin note
   * in `pages/ArrangementBuilder`. The CHOOSABLE set is still `activePolicies` below; this
   * only makes an archived policy an EXISTING order already points at resolvable. A
   * `Segmented` fails more quietly than a `Select`: an unmatched value highlights nothing at
   * all, so the operator sees no selection rather than a wrong one.
   */
  const policies = useQuery({
    queryKey: queryKeys.policies.list({ forStandingOrder: true, show: 'all' }),
    queryFn: ({ signal }) =>
      api.get<Paginated<PolicyListItemDto>>('/policies', {
        query: { page: 1, limit: PAGINATION.MAX_LIMIT, show: 'all' },
        signal,
      }),
  });
  /** What an already-stored `policyId` renders as — archived ones included. */
  const resolvePolicy = useMemo(() => {
    const byId = new Map((policies.data?.items ?? []).map((policy) => [policy.id, policy]));
    return (id: string): { name: string } | undefined => byId.get(id);
  }, [policies.data]);
  const activePolicies = useMemo(
    () => (policies.data?.items ?? []).filter((policy) => policy.archivedAt === null && policy.currentVersionId),
    [policies.data],
  );

  const existing = useQuery({
    queryKey: queryKeys.standingOrders.detail(standingOrderId ?? ''),
    queryFn: ({ signal }) => api.get<StandingOrderDto>(`/standing-orders/${standingOrderId ?? ''}`, { signal }),
    enabled: Boolean(standingOrderId),
  });

  const watched = Form.useWatch((values: unknown) => toStandingOrderDraft(values), form);
  const draft = watched ?? toStandingOrderDraft(emptyStandingOrder());

  /*
   * ⚠️ AUTO-SELECT THE ONLY ACTIVE POLICY — P3a's rule, and here it is load-bearing rather
   * than a nicety. The single-policy branch renders a HIDDEN `Form.Item`, so without this
   * the field is registered and permanently empty: "Choose a policy." would block the save
   * on a form with no policy control on it at all. Found by the test that asserts the
   * button names its first blocker.
   */
  useEffect(() => {
    if (draft.policyId !== '' || activePolicies.length !== 1) return;
    const only = activePolicies[0];
    if (!only) return;
    form.setFieldValue('policyId', only.id);
    notifyDevChange({
      where: 'Basics',
      what: `policy set to ${only.name}`,
      why: 'It is the only active policy. The single-policy branch renders a HIDDEN field, so without this the form could never be saved — "Choose a policy." would block it with no policy control on screen.',
    });
  }, [activePolicies, draft.policyId, form]);

  const selectedPolicy = activePolicies.find((policy) => policy.id === draft.policyId);
  const version = useQuery({
    queryKey: queryKeys.policies.version(draft.policyId, selectedPolicy?.currentVersionId ?? ''),
    queryFn: ({ signal }) =>
      api.get<PolicyVersionDto>(`/policies/${draft.policyId}/versions/${selectedPolicy?.currentVersionId ?? ''}`, {
        signal,
      }),
    enabled: Boolean(draft.policyId && selectedPolicy?.currentVersionId),
  });

  /** What the generator would really do — from the server, and only once there is an id. */
  const preview = useQuery({
    queryKey: queryKeys.standingOrders.generationPreview(standingOrderId ?? ''),
    queryFn: ({ signal }) =>
      api.get<StandingOrderPreviewDto>(`/standing-orders/${standingOrderId ?? ''}/generation-preview`, { signal }),
    enabled: Boolean(standingOrderId),
  });

  const grid = useMemo(() => {
    /* The same shape the PA intake builds — a version row carries its summary beside its id. */
    const postTypes = new Map<string, PostTypeSummaryDto>(
      (version.data?.postTypes ?? []).flatMap((row) => (row.postType ? [[row.postTypeId, row.postType] as const] : [])),
    );
    const vehicleTypes = new Map<string, VehicleTypeSummaryDto>(
      (version.data?.vehicleTypes ?? []).flatMap((row) =>
        row.vehicleType ? [[row.vehicleTypeId, row.vehicleType] as const] : [],
      ),
    );
    return toGrid(draft.requirementLines, { postTypes, vehicleTypes });
  }, [draft.requirementLines, version.data]);

  const orgUnits = useMemo(() => new Map<string, OrgUnitSummaryDto>(), []);

  const onValuesChange = useCallback(() => {
    setDirty(true);
  }, []);

  const appendRow = <K extends 'locations' | 'shifts'>(field: K, row: StandingOrderFormValues[K][number]): void => {
    const rows = (form.getFieldValue(field) ?? []) as StandingOrderFormValues[K];
    form.setFieldValue(field, [...rows, row]);
    onValuesChange();
  };

  const focusLastIn = useCallback((sectionId: string, selector: string): void => {
    requestAnimationFrame(() => {
      const nodes = sectionRefs.current.get(sectionId)?.querySelectorAll<HTMLElement>(selector);
      nodes?.[nodes.length - 1]?.focus();
    });
  }, []);

  const registerSection = useCallback((id: string, node: HTMLElement): void => {
    sectionRefs.current.set(id, node);
  }, []);

  const setRequirementLines = (lines: RequirementLineInputDto[]): void => {
    form.setFieldValue('requirementLines', lines);
    onValuesChange();
  };

  const sectionState: StandingOrderSectionState = {
    draft,
    endsKind: draft.endsKindRtc,
    grid,
    policies: activePolicies,
    resolvePolicy,
    orgUnits,
    horizonDays: preview.data?.horizonDays ?? null,
    fillOpenLeadHours: preview.data?.fillOpenLeadHours ?? null,
    readOnly,
  };
  const sectionActions: StandingOrderSectionActions = { appendRow, focusLastIn, setRequirementLines };

  const checklist = checklistOf(STANDING_ORDER_SECTIONS, sectionState);
  const blockers = checklist.flatMap((item) => {
    const descriptor = STANDING_ORDER_SECTIONS.find((section) => section.id === item.id);
    return descriptor ? [...descriptor.validate(sectionState).blockers] : [];
  });

  const save = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      standingOrderId
        ? api.patch<StandingOrderDto>(`/standing-orders/${standingOrderId}`, body)
        : api.post<StandingOrderDto>('/standing-orders', body),
    onSuccess: (saved) => {
      setDirty(false);
      void message.success(standingOrderId ? 'Saved' : 'Round-the-clock order started');
      void queryClient.invalidateQueries({ queryKey: queryKeys.standingOrders.all });
      if (!standingOrderId) void navigate(`/standing-orders/${saved.id}`, { replace: true });
    },
    onError: (error: ApiError) => void message.error(error.message),
  });

  const jump = (id: string): void => {
    const target = stepOfSection(STANDING_ORDER_SECTIONS, RTC_STEPS, id);
    if (target >= 0) setStep(target);
    const node = sectionRefs.current.get(id);
    // ⚠️ The optional CALL is required — jsdom has no `scrollIntoView`. See `HierarchyTable`.
    node?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    node?.querySelector<HTMLElement>('input, button, [tabindex]')?.focus();
  };

  useUnsavedGuard({
    when: dirty,
    confirm: () =>
      new Promise<boolean>((resolve) => {
        modal.confirm({
          title: RTC_COPY.unsavedGuard,
          okText: 'Leave',
          cancelText: 'Stay',
          onOk: () => {
            resolve(true);
          },
          onCancel: () => {
            resolve(false);
          },
        });
      }),
  });

  if (policies.isLoading || (standingOrderId && existing.isLoading)) {
    return (
      <div className="flex justify-center py-12">
        <Spin />
      </div>
    );
  }

  const initialValues = standingOrderId && existing.data ? fromStandingOrder(existing.data) : emptyStandingOrder();

  const submit = (): void => {
    const body = toStandingOrderBody(form.getFieldsValue(true) as StandingOrderFormValues);
    const run = (): void => {
      save.mutate(body);
    };
    /* ⚠️ On a LIVE order the confirmation says what happens to what already exists. */
    if (standingOrderId) {
      modal.confirm({
        title: 'Save these changes?',
        content: <span className="text-sm">{RTC_COPY.saveChangesActive}</span>,
        okText: 'Save',
        onOk: run,
      });
      return;
    }
    run();
  };

  return (
    <IntakeForm
      form={form}
      rightLabel="Checklist and order preview"
      initialValues={initialValues}
      disabled={readOnly}
      onValuesChange={onValuesChange}
      steps={RTC_STEPS}
      step={step}
      onStepChange={setStep}
      labels={{ back: 'Back', next: 'Next' }}
      /* [P4-UX] Step 1's required Name is refused at the step boundary, not two steps later. */
      gateNextOnStep
      sections={STANDING_ORDER_SECTIONS}
      state={sectionState}
      actions={sectionActions}
      registerSection={registerSection}
      header={
        <PageHeader
          icon={Repeat}
          parent={{ label: 'Round the clock', to: '/standing-orders' }}
          title={existing.data?.displayTitle ?? 'New round-the-clock order'}
          subtitle={existing.data?.reference}
        />
      }
      right={
        <div className="flex flex-col gap-4">
          <nav
            aria-label="Sections"
            data-testid="section-checklist"
            className="rounded-lg border border-border bg-card p-3"
          >
            <ul className="flex flex-col gap-1.5 text-sm">
              {checklist.map((item) => (
                <li key={item.id}>
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 text-left"
                    onClick={() => {
                      jump(item.id);
                    }}
                  >
                    <span className={item.done ? 'text-foreground' : 'text-muted-foreground'}>
                      {item.done ? '✓' : '○'} {item.label}
                    </span>
                    {item.detail !== undefined && <span className="text-xs text-muted-foreground">{item.detail}</span>}
                  </button>
                </li>
              ))}
            </ul>
          </nav>

          <div className="rounded-lg border border-border bg-card p-3" aria-live="polite" data-testid="will-create">
            <h2 className="mb-1 text-sm font-semibold tracking-tight">What this will create</h2>
            <p className="text-sm text-muted-foreground">
              {preview.data
                ? RTC_COPY.willGenerate({
                    willCreate: preview.data.willCreate,
                    horizonDays: preview.data.horizonDays,
                    isRolling: preview.data.isRolling,
                  })
                : standingOrderId
                  ? RTC_COPY.previewPending
                  : RTC_COPY.previewNotYet}
            </p>
            <p className="mt-2 text-xs text-muted-foreground">{RTC_COPY.rolling}</p>
          </div>
        </div>
      }
      footer={({ stepper }) => (
        <div
          className="-mx-4 -mb-6 flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-border bg-background px-4 py-3 sm:-mx-6 sm:px-6"
          data-testid="save-bar"
        >
          <div className="flex flex-wrap items-center gap-3">
            {stepper}
            {/*
              ⚠️ COLOUR *AND* WORDS — see the PA intake's bar for the whole of why.
              ⚠️ AND A NEW ORDER HAS NOT BEEN SAVED EVEN BEFORE IT IS DIRTY. Reading "Saved"
              on a form that has never been written anywhere is the one thing this indicator
              must never say.
            */}
            <span
              className={cn('text-xs', dirty || !standingOrderId ? 'text-destructive/80' : 'text-success/90')}
              aria-live="polite"
              data-testid="save-indicator"
            >
              {dirty || !standingOrderId ? RTC_COPY.unsaved : RTC_COPY.saved}
            </span>
            <span className="text-xs text-muted-foreground">{RTC_COPY.saveHint}</span>
          </div>
          <Button
            type="primary"
            loading={save.isPending}
            disabled={readOnly || blockers.length > 0}
            title={blockers[0] ?? undefined}
            data-testid="save-standing-order"
            onClick={submit}
          >
            {standingOrderId ? RTC_COPY.saveChanges : RTC_COPY.save}
          </Button>
        </div>
      )}
    >
      {readOnly && <Alert type="info" showIcon message="Your access is view-only." />}
    </IntakeForm>
  );
};

export default StandingOrderBuilderPage;
